import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { AdminDashboard } from './pages/AdminDashboard';
import AdminDashboardEditor from './pages/AdminDashboardEditor';
import { PlatformSettings } from './pages/admin/PlatformSettings';
import { NotFound } from './pages/NotFound';
import ComingSoon from './pages/ComingSoon';

// Progressive Release Lockdown - redirects non-admin users to dashboard
const PROGRESSIVE_RELEASE_LOCKDOWN = true;

const LockdownRedirect = ({ children }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  
  // During lockdown, redirect non-admin users to basic dashboard
  if (PROGRESSIVE_RELEASE_LOCKDOWN && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

import { Settings } from './pages/Settings';
import { UsersSettings } from './pages/settings/UsersSettings';
import { TeamsSettings } from './pages/settings/TeamsSettings';
import { CrewsSettings } from './pages/settings/CrewsSettings';
import { AboutSettings } from './pages/settings/AboutSettings';
import { CostsMargins } from './pages/settings/CostsMargins';
import { CompanySettings } from './pages/settings/CompanySettings';
import { SecuritySettings } from './pages/settings/SecuritySettings';
import { TemplatesSettings } from './pages/settings/TemplatesSettings';
import { OnboardingSettings } from './pages/settings/OnboardingSettings';
import { IntegrationsSettings } from './pages/settings/IntegrationsSettings';
import { EmailSettings } from './pages/settings/EmailSettings';
import { RolesSettings } from './pages/settings/RolesSettings';
import ActionApprovals from './pages/settings/ActionApprovals';
import PrimeDigestSettings from './pages/settings/PrimeDigestSettings';
import SoundsSettings from './pages/settings/SoundsSettings';
import NotificationSettings from './pages/settings/NotificationSettings';
import FinancingSettings from './pages/settings/FinancingSettings';
import { CustomFields } from './pages/CustomFields';
import { LeadDetail } from './pages/LeadDetail';
import { UnifiedRecordsWorkspace } from './pages/UnifiedRecordsWorkspace';
import ContactsNew from './pages/ContactsNew';
import ContactDetail from './pages/ContactDetail';
import ContactDetailEnhanced from './pages/ContactDetailEnhanced';
import WorkflowColorSettings from './pages/WorkflowColorSettings';
import JobFieldSettings from './pages/JobFieldSettings';
import DataTools from './pages/DataTools';
import { Board } from './pages/Board';
import { SalesBoard } from './pages/sales-hub/SalesBoard';
import { SalesDashboard } from './pages/sales-hub/Dashboard';
import { OperationsDashboard } from './pages/operations-hub/Dashboard';
import { OperationsProductionBoard } from './pages/operations-hub/OperationsProductionBoard';
import { JobQueue } from './pages/operations-hub/JobQueue';
import { CrewManagement } from './pages/operations-hub/CrewManagement';
import { Production } from './pages/operations-hub/Production';
import { RevenueProjections } from './pages/operations-hub/RevenueProjections';
import { ProjectMap } from './pages/operations-hub/ProjectMap';
import CapacityDashboard from './pages/operations-hub/CapacityDashboard';
import { WorkOrders } from './pages/operations-hub/WorkOrders';
import UnifiedBoard from './pages/UnifiedBoard';
import { MarketingDashboard } from './pages/marketing-hub/Dashboard';
import { Campaigns } from './pages/marketing-hub/Campaigns';
import { LeadSources } from './pages/marketing-hub/LeadSources';
import { Analytics } from './pages/marketing-hub/Analytics';
import { CheckIns } from './pages/marketing-hub/CheckIns';
import { ContentStudio } from './pages/marketing-hub/ContentStudio';
import { Scheduler } from './pages/marketing-hub/Scheduler';
import { Agency } from './pages/marketing-hub/Agency';
import { Assets } from './pages/marketing-hub/Assets';
import { MarketingSettings } from './pages/marketing-hub/Settings';
import { OnTrackPacing } from './pages/marketing-hub/OnTrackPacing';
import MarketingTracker from './pages/marketing-hub/MarketingTracker';
import AdsManager from './pages/marketing-hub/AdsManager';
import { FivePillarsPage } from './pages/governance/FivePillarsPage';
import FivePillarsHub from './pages/FivePillarsHub';
import DevCalendar from './pages/DevCalendar';
import CustomerLogin from './pages/CustomerLogin';
import CustomerPortal from './pages/CustomerPortal';
import { ThemeEditor } from './pages/ThemeEditor';
import { PageManager } from './pages/PageManager';
import { ContentManager } from './pages/ContentManager';
import { BrandingManager } from './pages/BrandingManager';
import { TestDynamicContent } from './pages/TestDynamicContent';
import { DeveloperNotes } from './pages/DeveloperNotes';
import Tasks from './pages/Tasks';
import Calendar from './pages/Calendar';
import CalendarV2 from './pages/CalendarV2';
import EOSDashboard from './pages/EOSDashboard';
import EOSScorecard from './pages/EOSScorecard';
import EOSLiveScorecard from './pages/EOSLiveScorecard';
import EOSRocks from './pages/EOSRocksSlider';
import EOSRocksOld from './pages/EOSRocks';
import EOSIssues from './pages/EOSIssuesSlider';
import EOSIssuesLegacy from './pages/EOSIssues';
import EOSTodos from './pages/EOSTodosSlider';
import EOSTodosOld from './pages/EOSTodos';
import EOSL10Meetings from './pages/EOSL10Meetings';
import EOSL10MeetingRoom from './pages/EOSL10MeetingRoom';
import EOSL10MeetingRoomEnhanced from './pages/EOSL10MeetingRoomEnhanced';
import L10VideoPopout from './pages/L10VideoPopout';
import EOSHeadlines from './pages/EOSHeadlinesSlider';
import EOSHeadlinesLegacy from './pages/EOSHeadlines';
import EOSAccountabilityChart from './pages/EOSAccountabilityChartSlider';
import EOSAccountabilityChartLegacy from './pages/EOSAccountabilityChart';
import EOSVTO from './pages/EOSVTO';
import EOSLeadershipDashboard from './pages/EOSLeadershipDashboard';
import PlatformDashboard from './pages/PlatformDashboard';
import FinanceHub from './pages/FinanceHub';
// Jobs.js removed - use UnifiedRecordsWorkspace instead
import JobCreate from './pages/JobCreate';
import CrewPortal from './pages/CrewPortal';
import CrewJobDetail from './pages/CrewJobDetail';
import { EstimateEditor } from './components/estimates/EstimateEditor';
import { PresentMode } from './components/estimates/PresentMode';
import DirectorOfSalesDashboard from './pages/DirectorOfSalesDashboard';
// Note: GeneralManagerDashboard removed per dashboard architecture reset
// Note: Old CEODashboard replaced with new executive dashboards
import { NewCEODashboard, PresidentDashboard } from './pages/dashboard';
import UserActivityLog from './pages/UserActivityLog';
import AgentWorkforce from './pages/AgentWorkforce';
import { SupportHub } from './pages/SupportHub';
import { SupportTicketDetail } from './pages/SupportTicketDetail';
import { MyTickets, MyTicketDetail } from './pages/MyTickets';
import { Onboarding } from './pages/Onboarding';
import { Chat } from './pages/Chat';
import Communications from './pages/Communications';
import EmailHub from './pages/EmailHubPage';
import SocialMedia from './pages/communications/SocialMedia';
import PlatformAI from './pages/PlatformAI';
import { MyTeam } from './pages/MyTeam';
import { ImpersonateRedirect } from './components/ImpersonateRedirect';
import { Toaster } from './components/ui/sonner';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { VideoCallProvider } from './contexts/VideoCallContext';
import { SoundProvider } from './contexts/SoundContext';
import GlobalVideoCall from './components/GlobalVideoCall';

// Call Center Module
import { CallCenterBoard, CallCenterInbox, CallCenterQueues, CallCenterAIAgents, CallCenterDialer, CallCenterScripts, CallCenterCallbacks, CallCenterAnalytics, CallCenterCallLog, CallCenterDashboards, DiscoveryControlPanel, ControlPanel, WorkStation, AIAnswerAgentsSettings, CallCenterHubSettings, RhinoVoice } from './pages/call-center';
import CallCenterSettings from './pages/settings/CallCenterSettings';
import LeadFlowOrchestrator from './pages/settings/LeadFlowOrchestrator';
import DemoDataManager from './pages/settings/DemoDataManager';

// Automations Module
import Automations from './pages/Automations';
import AutomationsHub from './pages/settings/AutomationsHub';
import ReportBuilder from './pages/ReportBuilder';

// Canvas (Collaborative Whiteboard)
import CanvasList from './pages/CanvasList';
import CanvasEditor from './pages/CanvasEditor';

// Competition Zone Module
import { CompetitionOverview, CompetitionDetail, CreateCompetition, Leaderboards, RhinoChallenges } from './pages/competition-zone';

// Onboarding Wizard
import { OnboardingWizardOverlay, OnboardingBanner } from './components/onboarding';

import './App.css';

// Redirect component for old /jobs/:jobId route
const JobRedirectToWorkspace = () => {
  const { jobId } = require('react-router-dom').useParams();
  return <Navigate to={`/workspace/records?source=sales&mode=projects&id=${jobId}`} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WebSocketProvider>
          <SoundProvider>
            <VideoCallProvider>
              <ThemeProvider>
                <ImpersonateRedirect />
                <Toaster position="top-right" />
                <GlobalVideoCall />
                <OnboardingWizardOverlay />
                <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin"
              element={
                <ProtectedRoute superAdminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/dashboard-editor"
              element={
                <ProtectedRoute superAdminOnly>
                  <AdminDashboardEditor />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/platform-settings"
              element={
                <ProtectedRoute superAdminOnly>
                  <PlatformSettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/theme-editor"
              element={
                <ProtectedRoute superAdminOnly>
                  <ThemeEditor />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/page-manager"
              element={
                <ProtectedRoute superAdminOnly>
                  <PageManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/content-manager"
              element={
                <ProtectedRoute superAdminOnly>
                  <ContentManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/branding-manager"
              element={
                <ProtectedRoute superAdminOnly>
                  <BrandingManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/test-dynamic-content"
              element={
                <ProtectedRoute superAdminOnly>
                  <TestDynamicContent />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/developer-notes"
              element={
                <ProtectedRoute>
                  <DeveloperNotes />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <Tasks />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <CalendarV2 />
                </ProtectedRoute>
              }
            />
            
            {/* Legacy Calendar (Old Version) */}
            <Route
              path="/calendar-legacy"
              element={
                <ProtectedRoute>
                  <Calendar />
                </ProtectedRoute>
              }
            />

            {/* EOS Hub Routes */}
            <Route
              path="/eos/dashboard"
              element={
                <ProtectedRoute>
                  <EOSDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/leadership"
              element={
                <ProtectedRoute>
                  <EOSLeadershipDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/scorecard"
              element={
                <ProtectedRoute>
                  <EOSLiveScorecard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/scorecard-legacy"
              element={
                <ProtectedRoute>
                  <EOSScorecard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/rocks"
              element={
                <ProtectedRoute>
                  <EOSRocks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/issues"
              element={
                <ProtectedRoute>
                  <EOSIssues />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/issues-legacy"
              element={
                <ProtectedRoute>
                  <EOSIssuesLegacy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/todos"
              element={
                <ProtectedRoute>
                  <EOSTodos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/l10-meetings"
              element={
                <ProtectedRoute>
                  <EOSL10Meetings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/l10-meetings/:meetingId"
              element={
                <ProtectedRoute>
                  <EOSL10MeetingRoomEnhanced />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/l10-meetings/:meetingId/classic"
              element={
                <ProtectedRoute>
                  <EOSL10MeetingRoom />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/l10-meetings/:meetingId/enhanced"
              element={
                <ProtectedRoute>
                  <EOSL10MeetingRoomEnhanced />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/l10-meetings/:meetingId/video-popout"
              element={
                <ProtectedRoute>
                  <L10VideoPopout />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/headlines"
              element={
                <ProtectedRoute>
                  <EOSHeadlines />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/headlines-legacy"
              element={
                <ProtectedRoute>
                  <EOSHeadlinesLegacy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/accountability-chart"
              element={
                <ProtectedRoute>
                  <EOSAccountabilityChart />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/accountability-chart-legacy"
              element={
                <ProtectedRoute>
                  <EOSAccountabilityChartLegacy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos/vto"
              element={
                <ProtectedRoute>
                  <EOSVTO />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eos"
              element={<Navigate to="/eos/dashboard" replace />}
            />
            {/* Legacy route redirects */}
            <Route path="/eos-hub" element={<Navigate to="/eos/dashboard" replace />} />
            <Route path="/admin/users" element={<Navigate to="/admin" replace />} />
            
            {/* Platform Dashboard (Super Admin) */}
            <Route
              path="/platform"
              element={
                <ProtectedRoute>
                  <PlatformDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Finance Hub - Phase 5 */}
            <Route
              path="/finance-hub"
              element={
                <ProtectedRoute>
                  <FinanceHub />
                </ProtectedRoute>
              }
            />
            
            {/* Jobs Routes - Redirect to Unified Workspace */}
            <Route
              path="/jobs"
              element={<Navigate to="/workspace/records?source=sales&mode=projects" replace />}
            />
            <Route
              path="/jobs/create"
              element={
                <ProtectedRoute>
                  <JobCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/:jobId"
              element={<JobRedirectToWorkspace />}
            />
            
            {/* Estimate Editor & Present Mode */}
            <Route
              path="/workspace/jobs/:jobId/estimates/:estimateId/edit"
              element={
                <ProtectedRoute>
                  <EstimateEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspace/jobs/:jobId/estimates/:estimateId/present"
              element={
                <ProtectedRoute>
                  <PresentMode />
                </ProtectedRoute>
              }
            />
            
            {/* Director of Sales Dashboard - Protected during lockdown */}
            <Route
              path="/director/dashboard"
              element={
                <ProtectedRoute>
                  <LockdownRedirect>
                    <DirectorOfSalesDashboard />
                  </LockdownRedirect>
                </ProtectedRoute>
              }
            />

            {/* CEO Dashboard - Protected during lockdown */}
            <Route
              path="/dashboard/ceo"
              element={
                <ProtectedRoute>
                  <LockdownRedirect>
                    <NewCEODashboard />
                  </LockdownRedirect>
                </ProtectedRoute>
              }
            />

            {/* President Dashboard - Protected during lockdown */}
            <Route
              path="/dashboard/president"
              element={
                <ProtectedRoute>
                  <LockdownRedirect>
                    <PresidentDashboard />
                  </LockdownRedirect>
                </ProtectedRoute>
              }
            />

            {/* Legacy CEO route - redirect to dashboard during lockdown */}
            <Route
              path="/ceo/dashboard"
              element={<LockdownRedirect><Navigate to="/dashboard/ceo" replace /></LockdownRedirect>}
            />

            {/* User Activity Log */}
            <Route
              path="/ceo/user-activity"
              element={
                <ProtectedRoute>
                  <UserActivityLog />
                </ProtectedRoute>
              }
            />

            {/* Rhino-1 Enterprise - Super Admin only */}
            <Route
              path="/super-admin/agent-workforce"
              element={
                <ProtectedRoute>
                  <AgentWorkforce />
                </ProtectedRoute>
              }
            />

            {/* Platform AI - Super Admin only */}
            <Route
              path="/platform-ai"
              element={
                <ProtectedRoute>
                  <PlatformAI />
                </ProtectedRoute>
              }
            />

            {/* Automations Center - Legacy route */}
            <Route
              path="/automations"
              element={
                <ProtectedRoute>
                  <Automations />
                </ProtectedRoute>
              }
            />

            {/* Automations Hub - Admin Settings entry point */}
            <Route
              path="/settings/automations"
              element={
                <ProtectedRoute>
                  <AutomationsHub />
                </ProtectedRoute>
              }
            />

            {/* Competition Zone */}
            <Route
              path="/competition"
              element={
                <ProtectedRoute>
                  <CompetitionOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/competition/overview"
              element={
                <ProtectedRoute>
                  <CompetitionOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/competition/competitions/create"
              element={
                <ProtectedRoute>
                  <CreateCompetition />
                </ProtectedRoute>
              }
            />
            <Route
              path="/competition/competitions/:competitionId"
              element={
                <ProtectedRoute>
                  <CompetitionDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/competition/leaderboards"
              element={
                <ProtectedRoute>
                  <Leaderboards />
                </ProtectedRoute>
              }
            />
            <Route
              path="/competition/challenges"
              element={
                <ProtectedRoute>
                  <RhinoChallenges />
                </ProtectedRoute>
              }
            />

            {/* Support Hub - Admin only */}
            <Route
              path="/support-hub"
              element={
                <ProtectedRoute>
                  <SupportHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support-hub/tickets/:ticketId"
              element={
                <ProtectedRoute>
                  <SupportTicketDetail />
                </ProtectedRoute>
              }
            />

            {/* My Tickets - User view */}
            <Route
              path="/my-tickets"
              element={
                <ProtectedRoute>
                  <MyTickets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-tickets/:ticketId"
              element={
                <ProtectedRoute>
                  <MyTicketDetail />
                </ProtectedRoute>
              }
            />

            {/* Onboarding Settings - Super Admin only */}
            <Route
              path="/settings/onboarding"
              element={
                <ProtectedRoute superAdminOnly>
                  <OnboardingSettings />
                </ProtectedRoute>
              }
            />

            {/* AI Action Approvals - Admin only */}
            <Route
              path="/settings/action-approvals"
              element={
                <ProtectedRoute>
                  <ActionApprovals />
                </ProtectedRoute>
              }
            />

            {/* Prime Digest Settings */}
            <Route
              path="/settings/prime-digest"
              element={
                <ProtectedRoute>
                  <PrimeDigestSettings />
                </ProtectedRoute>
              }
            />

            {/* Sound Notifications Settings */}
            <Route
              path="/settings/sounds"
              element={
                <ProtectedRoute>
                  <SoundsSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/notifications"
              element={
                <ProtectedRoute>
                  <NotificationSettings />
                </ProtectedRoute>
              }
            />

            {/* General Manager Dashboard - Protected during lockdown */}
            {/* Redirect to President Dashboard as closest equivalent */}
            <Route
              path="/gm/dashboard"
              element={<LockdownRedirect><Navigate to="/dashboard/president" replace /></LockdownRedirect>}
            />

            {/* Crew Portal Routes */}
            <Route
              path="/crew"
              element={
                <ProtectedRoute>
                  <CrewPortal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crew/jobs/:jobId"
              element={
                <ProtectedRoute>
                  <CrewJobDetail />
                </ProtectedRoute>
              }
            />
            
            {/* Settings Routes */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/users"
              element={
                <ProtectedRoute adminOnly>
                  <UsersSettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/teams"
              element={
                <ProtectedRoute adminOnly>
                  <TeamsSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/crews"
              element={
                <ProtectedRoute adminOnly>
                  <CrewsSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/financing"
              element={
                <ProtectedRoute adminOnly>
                  <FinancingSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/costs-margins"
              element={
                <ProtectedRoute adminOnly>
                  <CostsMargins />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/workflow-colors"
              element={
                <ProtectedRoute adminOnly>
                  <WorkflowColorSettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/job-fields"
              element={
                <ProtectedRoute adminOnly>
                  <JobFieldSettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/data-tools"
              element={
                <ProtectedRoute adminOnly>
                  <DataTools />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/company"
              element={
                <ProtectedRoute adminOnly>
                  <CompanySettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/security"
              element={
                <ProtectedRoute adminOnly>
                  <SecuritySettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/templates"
              element={
                <ProtectedRoute adminOnly>
                  <TemplatesSettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/integrations"
              element={
                <ProtectedRoute superAdminOnly>
                  <IntegrationsSettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/email"
              element={
                <ProtectedRoute superAdminOnly>
                  <EmailSettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/lead-flow"
              element={
                <ProtectedRoute superAdminOnly>
                  <LeadFlowOrchestrator />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/demo-data"
              element={
                <ProtectedRoute superAdminOnly>
                  <DemoDataManager />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/roles"
              element={
                <ProtectedRoute>
                  <RolesSettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/about"
              element={
                <ProtectedRoute adminOnly>
                  <AboutSettings />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/settings/custom-fields"
              element={
                <ProtectedRoute superAdminOnly>
                  <CustomFields />
                </ProtectedRoute>
              }
            />
            
            {/* Legacy route - redirect to Unified Workspace */}
            <Route
              path="/contacts"
              element={<Navigate to="/workspace/records?source=sales&mode=leads" replace />}
            />
            
            {/* Unified Records Workspace - Cross-Hub Lead/Project Management */}
            <Route
              path="/workspace/records"
              element={
                <ProtectedRoute>
                  <UnifiedRecordsWorkspace />
                </ProtectedRoute>
              }
            />
            
            {/* Legacy route redirect */}
            <Route path="/leads" element={<Navigate to="/workspace/records?source=sales&mode=leads" replace />} />
            
            <Route
              path="/leads/:id"
              element={
                <ProtectedRoute>
                  <LeadDetail />
                </ProtectedRoute>
              }
            />
            
            {/* New Contacts Module Routes */}
            <Route
              path="/contacts-new"
              element={
                <ProtectedRoute>
                  <ContactsNew />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contacts/:contactId"
              element={
                <ProtectedRoute>
                  <ContactDetailEnhanced />
                </ProtectedRoute>
              }
            />
            
            {/* Unified Board - Sales & Operations Pipeline */}
            <Route
              path="/board"
              element={
                <ProtectedRoute>
                  <UnifiedBoard />
                </ProtectedRoute>
              }
            />
            
            {/* Sales Hub Routes */}
            <Route
              path="/sales-hub/dashboard"
              element={
                <ProtectedRoute>
                  <SalesDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Sales Hub Board - New unified sales board */}
            <Route
              path="/sales-hub/board"
              element={
                <ProtectedRoute>
                  <SalesBoard />
                </ProtectedRoute>
              }
            />
            
            {/* Redirect old sales hub jobs to unified workspace */}
            <Route path="/sales-hub/jobs" element={<Navigate to="/workspace/records?source=sales&mode=projects" replace />} />
            <Route path="/sales-hub/jobs/:jobId" element={<JobRedirectToWorkspace />} />
            
            {/* Sales Pipeline - redirect to new sales board */}
            <Route path="/sales-hub/pipeline" element={<Navigate to="/sales-hub/board" replace />} />
            <Route path="/board" element={<Navigate to="/sales-hub/board" replace />} />
            
            {/* Operations Hub Routes */}
            <Route
              path="/operations-hub/dashboard"
              element={
                <ProtectedRoute>
                  <OperationsDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Operations Production Board - New Kanban board for production workflow */}
            <Route
              path="/operations-hub/production-board"
              element={
                <ProtectedRoute>
                  <OperationsProductionBoard />
                </ProtectedRoute>
              }
            />
            
            {/* Legacy route redirect */}
            <Route
              path="/operations-hub/board"
              element={
                <Navigate to="/operations-hub/production-board" replace />
              }
            />
            
            {/* Redirect old operations hub queue to unified workspace */}
            <Route path="/operations-hub/queue" element={<Navigate to="/workspace/records?source=ops&mode=projects" replace />} />
            
            <Route
              path="/operations-hub/capacity"
              element={
                <ProtectedRoute>
                  <CapacityDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operations-hub/crew"
              element={
                <ProtectedRoute>
                  <CrewManagement />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operations-hub/work-orders"
              element={
                <ProtectedRoute>
                  <WorkOrders />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operations-hub/production"
              element={
                <ProtectedRoute>
                  <Production />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operations-hub/revenue-projections"
              element={
                <ProtectedRoute>
                  <RevenueProjections />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/operations-hub/project-map"
              element={
                <ProtectedRoute>
                  <ProjectMap />
                </ProtectedRoute>
              }
            />
            
            {/* Marketing Hub Routes */}
            <Route
              path="/marketing-hub/dashboard"
              element={
                <ProtectedRoute>
                  <MarketingDashboard />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/marketing-hub/pacing"
              element={
                <ProtectedRoute>
                  <OnTrackPacing />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/marketing-hub/tracker"
              element={
                <ProtectedRoute>
                  <MarketingTracker />
                </ProtectedRoute>
              }
            />

            <Route
              path="/marketing-hub/ads-manager"
              element={
                <ProtectedRoute>
                  <AdsManager />
                </ProtectedRoute>
              }
            />

            <Route
              path="/report-builder"
              element={
                <ProtectedRoute>
                  <ReportBuilder />
                </ProtectedRoute>
              }
            />

            {/* Canvas - Collaborative Whiteboard */}
            <Route
              path="/canvas"
              element={
                <ProtectedRoute>
                  <CanvasList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/canvas/:canvasId"
              element={
                <ProtectedRoute>
                  <CanvasEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/governance/five-pillars"
              element={
                <ProtectedRoute>
                  <FivePillarsPage />
                </ProtectedRoute>
              }
            />

            {/* Five Pillars Execution Hub */}
            <Route
              path="/five-pillars"
              element={
                <ProtectedRoute>
                  <FivePillarsHub />
                </ProtectedRoute>
              }
            />
            
            {/* Development Calendar */}
            <Route
              path="/dev-calendar"
              element={
                <ProtectedRoute>
                  <DevCalendar />
                </ProtectedRoute>
              }
            />
            
            {/* Customer Portal (Public routes - no admin auth required) */}
            <Route path="/customer-login" element={<CustomerLogin />} />
            <Route path="/customer-portal" element={<CustomerPortal />} />
            
            <Route
              path="/marketing-hub/campaigns"
              element={
                <ProtectedRoute>
                  <Campaigns />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/marketing-hub/lead-sources"
              element={
                <ProtectedRoute>
                  <LeadSources />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/marketing-hub/analytics"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/marketing-hub/check-ins"
              element={
                <ProtectedRoute>
                  <CheckIns />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/marketing-hub/content-studio"
              element={
                <ProtectedRoute>
                  <ContentStudio />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/marketing-hub/scheduler"
              element={
                <ProtectedRoute>
                  <Scheduler />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/marketing-hub/agency"
              element={
                <ProtectedRoute>
                  <Agency />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/marketing-hub/assets"
              element={
                <ProtectedRoute>
                  <Assets />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/marketing-hub/settings"
              element={
                <ProtectedRoute>
                  <MarketingSettings />
                </ProtectedRoute>
              }
            />
            
            {/* Coming Soon Pages - Pipeline */}
            <Route
              path="/pipeline/opportunities"
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pipeline/quotes"
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />
            
            {/* Coming Soon Pages - Production */}
            <Route
              path="/production/work-orders"
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />
            <Route
              path="/production/qa"
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />
            
            {/* Coming Soon Pages - Marketing */}
            <Route
              path="/marketing/content"
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketing/reviews"
              element={
                <ProtectedRoute>
                  <ComingSoon />
                </ProtectedRoute>
              }
            />
            
            {/* Chat */}
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              }
            />
            
            {/* Communications Hub */}
            <Route
              path="/communications"
              element={
                <ProtectedRoute>
                  <Communications />
                </ProtectedRoute>
              }
            />
            
            {/* Social Media Communications */}
            <Route
              path="/communications/social-media"
              element={
                <ProtectedRoute>
                  <SocialMedia />
                </ProtectedRoute>
              }
            />
            
            {/* Email Hub */}
            <Route
              path="/email"
              element={
                <ProtectedRoute>
                  <EmailHub />
                </ProtectedRoute>
              }
            />
            
            {/* Call Center Module */}
            <Route
              path="/call-center"
              element={
                <ProtectedRoute>
                  <CallCenterBoard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/board"
              element={<Navigate to="/call-center" replace />}
            />
            <Route
              path="/call-center/inbox"
              element={
                <ProtectedRoute>
                  <CallCenterInbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/queues"
              element={
                <ProtectedRoute>
                  <CallCenterQueues />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/ai-agents"
              element={
                <ProtectedRoute>
                  <CallCenterAIAgents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/dialer"
              element={
                <ProtectedRoute>
                  <CallCenterDialer />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/scripts"
              element={
                <ProtectedRoute>
                  <CallCenterScripts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/callbacks"
              element={
                <ProtectedRoute>
                  <CallCenterCallbacks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/analytics"
              element={
                <ProtectedRoute>
                  <CallCenterAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/call-log"
              element={
                <ProtectedRoute>
                  <CallCenterCallLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/dashboards"
              element={
                <ProtectedRoute>
                  <CallCenterDashboards />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/discovery"
              element={
                <ProtectedRoute>
                  <DiscoveryControlPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/control-panel"
              element={
                <ProtectedRoute>
                  <ControlPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/rhino-voice"
              element={
                <ProtectedRoute>
                  <RhinoVoice />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/work-station"
              element={
                <ProtectedRoute>
                  <WorkStation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/ai-answer-settings"
              element={
                <ProtectedRoute>
                  <AIAnswerAgentsSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/call-center/settings/*"
              element={
                <ProtectedRoute>
                  <CallCenterHubSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/call-center"
              element={
                <ProtectedRoute superAdminOnly>
                  <CallCenterSettings />
                </ProtectedRoute>
              }
            />
            
            {/* My Team - For Managers */}
            <Route
              path="/my-team"
              element={
                <ProtectedRoute>
                  <MyTeam />
                </ProtectedRoute>
              }
            />
            
            {/* Teams Page - For Super Admin */}
            <Route
              path="/teams"
              element={
                <ProtectedRoute>
                  <TeamsSettings />
                </ProtectedRoute>
              }
            />
            
            {/* Root redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
            </ThemeProvider>
          </VideoCallProvider>
          </SoundProvider>
        </WebSocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;